import type { MintInfo } from "@solana/spl-token";
import { createTransferInstruction, TOKEN_PROGRAM_ID } from "@solana/spl-token0.3.8";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection, Signer, TransactionInstruction } from "@solana/web3.js";
import { PublicKey, SystemProgram } from "@solana/web3.js";
import { fromLamports, NATIVE_SOL, SYSTEM_PROGRAM_ID, toLamports } from "@stepfinance/common";
import { deformatNumber } from "@stepfinance/react";
import { type NormalizedTokenAccount, TokenAccountParser } from "@stepfinance/tokens";
import type { ChangeEventHandler } from "react";
import { useEffect, useState } from "react";

import { findOrCreateAccountByMint } from "../../actions";
import { confirmOptions } from "../../constants/transaction";
import { useAccountsContextRefresher } from "../../contexts/accounts";
import type { ConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { useConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { cache } from "../../utils/cache";
import { notify } from "../../utils/notifications";
import { getTokenByMint } from "../../utils/tokenInfo";
import { handlePreSendTxnError, sendTransaction } from "../../utils/transactions";
import { validateAddressInput } from "../../utils/utils";
import { TokenChangeNotificationDisplay } from "../Notifications/TokenChangeNotificationDisplay";

export interface SendableToken {
  mint: string;
  mintInfo: MintInfo;
  account: NormalizedTokenAccount;
  balance: number;
}

const transferTokens = async (
  connection: Connection,
  wallet: ConnectedWallet,
  fromAccountInfo: NormalizedTokenAccount,
  toAddress: PublicKey,
  lamports: number,
  onTransactionInProgress: () => void,
) => {
  if (!wallet.publicKey) {
    throw new Error("Please connect wallet");
  }
  const fromTokenMint = new PublicKey(fromAccountInfo.info.mint);
  const fromTokenAddress = new PublicKey(fromAccountInfo.pubkey);

  if (fromTokenAddress) {
    const signers: Signer[] = [];
    const createAccountInstructions: TransactionInstruction[] = [];
    const createAccountCleanupInstructions: TransactionInstruction[] = [];
    let transferInstructions: TransactionInstruction[] = [];

    const toAddressInfo = await connection.getAccountInfo(toAddress);
    const isTokenAccount = !!toAddressInfo && toAddressInfo.owner.equals(TOKEN_PROGRAM_ID);
    // Wallet that has never received SOL will have a null toAddressInfo
    const isSystemAccount = !toAddressInfo || toAddressInfo.owner.equals(SYSTEM_PROGRAM_ID);
    const isNativeSOLTransfer = fromTokenMint.equals(NATIVE_SOL);
    const fromSymbol: string = getTokenByMint(fromTokenMint.toBase58())?.symbol ?? "???";

    if (isTokenAccount) {
      const account = TokenAccountParser(toAddress, toAddressInfo);
      const toTokenMatchesFromToken = account.info.mint.equals(fromTokenMint);
      const toSymbol: string = getTokenByMint(account.info.mint.toBase58())?.symbol ?? "???";

      // E.g. Attempting to send STEP directly to a USDC token account
      if (!toTokenMatchesFromToken) {
        notify({
          message: "Error sending funds",
          type: "error",
          description: `Cannot send ${fromSymbol} to a ${toSymbol} token account`,
        });
        return;
      }

      transferInstructions = [
        createTransferInstruction(fromTokenAddress, toAddress, wallet.publicKey, lamports, signers, TOKEN_PROGRAM_ID),
      ];
    } else if (isNativeSOLTransfer) {
      if (isSystemAccount) {
        transferInstructions = [
          SystemProgram.transfer({
            fromPubkey: fromTokenAddress,
            toPubkey: toAddress,
            lamports,
          }),
        ];
      } else {
        // User is trying to send to an account owned by a program that is not the system program
        // a.k.a. a non-native account
        notify({
          message: "Error sending funds",
          type: "error",
          description: "Address is not a valid native Solana address",
        });
        return;
      }
    } else {
      const toTokenAddress = await findOrCreateAccountByMint(
        connection,
        wallet.publicKey,
        toAddress,
        createAccountInstructions,
        fromTokenMint,
        undefined,
        true, // Non-system accounts are by definition off-curve
      );
      transferInstructions = [
        createTransferInstruction(fromTokenAddress, toTokenAddress, wallet.publicKey, lamports, [], TOKEN_PROGRAM_ID),
      ];
    }

    return sendTransaction(
      connection,
      wallet,
      [...createAccountInstructions, ...transferInstructions, ...createAccountCleanupInstructions],
      signers,
      true,
      confirmOptions,
      true,
      {
        success: {
          message: "Your asset has been sent",
          description: (
            <>
              <span>You sent:</span>
              <TokenChangeNotificationDisplay
                amount={fromLamports(lamports, fromAccountInfo.tokenAmount.decimals)}
                type="decrease"
                mintAddress={fromTokenMint}
              />
              <span>To:</span>
              <span>{toAddress.toBase58()}</span>
            </>
          ),
        },
      },
      onTransactionInProgress,
    );
  }
};

export const useSendToken = (
  fromAccount: NormalizedTokenAccount | null,
  closeModal: () => void,
  onSuccess: () => void = () => {},
): {
  sendAmount: string;
  setSendAmount(amount: string): void;
  sendMint: PublicKey;
  toAddressText: string;
  toAddress: PublicKey | null;
  onAddressChange: ChangeEventHandler<HTMLInputElement>;
  disableSend: boolean;
  handleSend(): void;
  sendButtonText: string;
  sendMintDecimals?: number;
} => {
  const { connection } = useConnection();
  const refreshAccounts = useAccountsContextRefresher();
  const wallet = useConnectedWallet();
  const [sendAmount, setSendAmount] = useState("");
  const [toAddressText, setToAddressText] = useState("");
  const [toAddress, setToAddress] = useState<PublicKey | null>(null);
  const sendMint = new PublicKey(fromAccount ? fromAccount.info.mint : "");
  const userBalance = fromAccount ? fromAccount.tokenAmount.uiAmount : 0;
  const [hasSufficientBalance, setHasSufficientBalance] = useState<boolean>(true);
  const [transactionPending, setTransactionPending] = useState(false);
  const disableSend = !toAddress || !hasSufficientBalance || transactionPending || !sendAmount;

  useEffect(() => {
    if (sendAmount && deformatNumber(sendAmount) > userBalance) {
      setHasSufficientBalance(false);
    } else {
      setHasSufficientBalance(true);
    }
  }, [sendAmount, userBalance]);

  useEffect(() => {
    setSendAmount("");
  }, [fromAccount]);

  const handleSend = () => {
    if (sendAmount && wallet && toAddress && fromAccount) {
      setTransactionPending(true);
      transferTokens(
        connection,
        wallet,
        fromAccount,
        toAddress,
        toLamports(sendAmount, cache.getMint(sendMint)),
        closeModal,
      )
        .catch((e) => {
          handlePreSendTxnError(e as Error, "Error sending funds");
          closeModal();
        })
        .finally(() => {
          setTransactionPending(false);
          refreshAccounts();
          onSuccess();
        });
    }
  };

  let sendButtonText = "Send";

  if (!sendAmount) {
    sendButtonText = "Enter an Amount";
  } else if (!hasSufficientBalance) {
    sendButtonText = "Insufficient Balance";
  } else if (!toAddress) {
    sendButtonText = "Enter an Address";
  } else if (transactionPending) {
    sendButtonText = "Approve Transaction in Wallet";
  }

  const onAddressChange = (e) => {
    setToAddressText(e.target.value);
    void validateAddressInput(e.target.value, connection).then(setToAddress);
  };

  return {
    sendAmount,
    setSendAmount,
    sendMint,
    toAddressText,
    toAddress,
    onAddressChange,
    disableSend,
    handleSend,
    sendButtonText,
    sendMintDecimals: fromAccount?.tokenAmount.decimals,
  };
};
